import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import Swal from "sweetalert2";
import LayersIcon from '@mui/icons-material/Layers';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';

const ControlHologramasSection = ({
  hologramaRecords = [],
  terminadoRecords = [],
  isFormOpen,
  setIsFormOpen,
  idEnvasadoTerminado,
  setIdEnvasadoTerminado,
  fechaMovimiento,
  setFechaMovimiento,
  marca,
  setMarca,
  loteEnvasado,
  setLoteEnvasado,
  capacidad,
  setCapacidad,
  invInicialRango,
  setInvInicialRango,
  invInicialCant,
  setInvInicialCant,
  entradaRango,
  setEntradaRango,
  entradaCant,
  setEntradaCant,
  salidaRango,
  setSalidaRango,
  salidaCant,
  setSalidaCant,
  invFinalRango,
  setInvFinalRango,
  invFinalCant,
  setInvFinalCant,
  mermasRango,
  setMermasRango,
  mermasCant,
  setMermasCant,
  observaciones,
  setObservaciones,
  handleSaveHologramas,
  onRegistrarMovimiento
}) => {
  // Modal de Movimientos de Hologramas (Salidas)
  const [isMovimientosModalOpen, setIsMovimientosModalOpen] = useState(false);
  const [selectedHolograma, setSelectedHolograma] = useState(null);

  // Campos del formulario de movimiento de salida (Igual a la imagen)
  const [movRangoConsumido, setMovRangoConsumido] = useState("");
  const [movCantidadConsumida, setMovCantidadConsumida] = useState("");
  const [movRangoMermas, setMovRangoMermas] = useState("N/A");
  const [movCantidadMermada, setMovCantidadMermada] = useState("0");
  const [movObservaciones, setMovObservaciones] = useState("Hologramas aplicados e inventario actualizado.");

  // Asistente rápido por prefijo y folios inicial/final
  const [asistentePrefijo, setAsistentePrefijo] = useState("A");
  const [asistenteFolioIni, setAsistenteFolioIni] = useState("");
  const [asistenteFolioFin, setAsistenteFolioFin] = useState("");
  const [usarAsistente, setUsarAsistente] = useState(false);

  // Auto-fill fields if Finished Bottling record is selected
  useEffect(() => {
    if (idEnvasadoTerminado && idEnvasadoTerminado !== "manual") {
      const selected = terminadoRecords.find(r => r.id === idEnvasadoTerminado);
      if (selected) {
        setMarca(selected.marca);
        setLoteEnvasado(selected.loteEnvasado);
        setCapacidad(selected.capacidad);
      }
    }
  }, [idEnvasadoTerminado, terminadoRecords, setMarca, setLoteEnvasado, setCapacidad]);

  // Manejo del asistente de folios
  const handleCalcularRangoAsistente = (pref, ini, fin) => {
    setAsistentePrefijo(pref);
    setAsistenteFolioIni(ini);
    setAsistenteFolioFin(fin);

    if (ini && fin) {
      const numIni = parseInt(ini, 10);
      const numFin = parseInt(fin, 10);
      if (!isNaN(numIni) && !isNaN(numFin) && numFin >= numIni) {
        const cant = numFin - numIni + 1;
        const padIni = String(numIni).padStart(ini.length, "0");
        const padFin = String(numFin).padStart(fin.length, "0");
        setMovRangoConsumido(`${pref}${padIni} - ${pref}${padFin}`);
        setMovCantidadConsumida(String(cant));
      }
    }
  };

  // Abrir modal de movimientos para un registro específico
  const handleOpenMovimientosModal = (rec) => {
    setSelectedHolograma(rec);
    
    // Extraer prefijo por defecto del rango inicial o disponible
    const rangoOrigen = rec.invFinalRango || rec.invInicialRango || "";
    const matchPref = rangoOrigen.match(/^([A-Za-z]+)/);
    const prefijoDetectado = matchPref ? matchPref[1] : "A";
    setAsistentePrefijo(prefijoDetectado);

    setMovRangoConsumido("");
    setMovCantidadConsumida("");
    setMovRangoMermas("N/A");
    setMovCantidadMermada("0");
    setMovObservaciones("Hologramas aplicados e inventario actualizado.");
    setAsistenteFolioIni("");
    setAsistenteFolioFin("");
    setUsarAsistente(false);

    setIsMovimientosModalOpen(true);
  };

  // Guardar movimiento de salida de hologramas
  const handleGuardarMovimiento = (e) => {
    if (e) e.preventDefault();

    if (!selectedHolograma) return;

    if (!movRangoConsumido || !movRangoConsumido.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Rango Consumido Requerido",
        text: "Por favor indique el rango de hologramas consumidos (ej. A0123 - A0223).",
        confirmButtonColor: "#059669"
      });
      return;
    }

    const cantConsumida = parseInt(movCantidadConsumida, 10);
    if (isNaN(cantConsumida) || cantConsumida <= 0) {
      Swal.fire({
        icon: "warning",
        title: "Cantidad Inválida",
        text: "La cantidad consumida debe ser un número entero mayor a 0.",
        confirmButtonColor: "#059669"
      });
      return;
    }

    const cantMermada = parseInt(movCantidadMermada, 10) || 0;
    const totalDescuento = cantConsumida + cantMermada;

    // Calcular saldo actual disponible
    const saldoActual = selectedHolograma.invFinalCant !== undefined && selectedHolograma.invFinalCant !== null
      ? Number(selectedHolograma.invFinalCant)
      : Number(selectedHolograma.invInicialCant || 0);

    if (totalDescuento > saldoActual && saldoActual > 0) {
      Swal.fire({
        icon: "warning",
        title: "Saldo Insuficiente",
        text: `La suma de consumos (${cantConsumida}) y mermas (${cantMermada}) supera el saldo disponible actual (${saldoActual}).`,
        confirmButtonColor: "#D97706"
      });
      return;
    }

    const nuevoSaldoCant = Math.max(0, saldoActual - totalDescuento);

    // Intentar deducir nuevo rango final
    let nuevoRangoFinal = selectedHolograma.invFinalRango || selectedHolograma.invInicialRango;
    const matchFin = movRangoConsumido.match(/-\s*([A-Za-z]+)?(\d+)/);
    if (matchFin) {
      const pref = matchFin[1] || asistentePrefijo || "A";
      const numFinConsumo = parseInt(matchFin[2], 10);
      const siguienteFolio = numFinConsumo + 1;
      const padSig = String(siguienteFolio).padStart(matchFin[2].length, "0");
      
      const matchRangoTotalFin = (selectedHolograma.invInicialRango || "").match(/-\s*([A-Za-z]+)?(\d+)/);
      if (matchRangoTotalFin) {
        nuevoRangoFinal = `${pref}${padSig} - ${matchRangoTotalFin[1] || pref}${matchRangoTotalFin[2]}`;
      }
    }

    const movimientoData = {
      fecha: new Date().toISOString().split("T")[0],
      rangoConsumido: movRangoConsumido.trim(),
      cantidadConsumida: cantConsumida,
      rangoMermas: movRangoMermas.trim() || "N/A",
      cantidadMermada: cantMermada,
      observaciones: movObservaciones.trim() || "Hologramas aplicados e inventario actualizado.",
      saldoRestanteRango: nuevoRangoFinal,
      saldoRestanteCant: nuevoSaldoCant
    };

    // Actualizar en el estado global
    if (onRegistrarMovimiento) {
      onRegistrarMovimiento(selectedHolograma.id, movimientoData);
    }

    // Actualizar copia local de selectedHolograma para reflejar en el modal
    const nuevoMovObj = {
      id: `MOV-${Date.now()}`,
      ...movimientoData
    };

    const movimientosActualizados = [...(selectedHolograma.movimientos || []), nuevoMovObj];
    const totalSalidas = (Number(selectedHolograma.salidaCant) || 0) + cantConsumida;
    const totalMermas = (Number(selectedHolograma.mermasCant) || 0) + cantMermada;

    setSelectedHolograma({
      ...selectedHolograma,
      salidaRango: movRangoConsumido.trim(),
      salidaCant: totalSalidas,
      mermasRango: movRangoMermas.trim() || selectedHolograma.mermasRango,
      mermasCant: totalMermas,
      invFinalRango: nuevoRangoFinal,
      invFinalCant: nuevoSaldoCant,
      observaciones: movObservaciones.trim(),
      movimientos: movimientosActualizados
    });

    Swal.fire({
      icon: "success",
      title: "¡Movimiento Registrado!",
      html: `
        <div style="font-size: 14px; text-align: left; line-height: 1.6;">
          <p><strong>Rango Consumido:</strong> ${movRangoConsumido}</p>
          <p><strong>Cantidad Consumida:</strong> ${cantConsumida} hologramas</p>
          <p><strong>Mermas:</strong> ${cantMermada} (${movRangoMermas})</p>
          <hr style="margin: 8px 0; border: none; border-top: 1px solid #E2E8F0;" />
          <p style="color: #059669; font-weight: 700;"><strong>Nuevo Saldo Disponible:</strong> ${nuevoSaldoCant} hologramas</p>
        </div>
      `,
      confirmButtonColor: "#059669"
    });

    // Reset de campos del formulario
    setMovRangoConsumido("");
    setMovCantidadConsumida("");
    setMovRangoMermas("N/A");
    setMovCantidadMermada("0");
    setAsistenteFolioIni("");
    setAsistenteFolioFin("");
  };

  if (!isFormOpen) {
    return (
      <div className="animate-fadeIn">
        <div className="history-section-header">
          <h2>Control del Inventario de Hologramas (Sección V)</h2>
        </div>

        <div className="records-list-wrapper">
          <table className="records-table">
            <thead>
              <tr>
                <th colSpan="5" style={{ textAlign: "center", background: "rgba(74, 93, 35, 0.2)", color: "var(--agave-green)" }}>
                  Datos Generales / Relación de Envasado
                </th>
                <th colSpan="3" style={{ textAlign: "center", background: "rgba(107, 33, 168, 0.15)", color: "#6B21A8" }}>
                  Control de Hologramas
                </th>
                <th colSpan="2" style={{ textAlign: "center" }}>Finalización</th>
                <th colSpan="1" style={{ textAlign: "center", background: "rgba(5, 150, 105, 0.15)", color: "#059669" }}>
                  Acciones
                </th>
              </tr>
              <tr>
                <th>Ref. Envasado</th>
                <th>Fecha Mov.</th>
                <th>Marca</th>
                <th>Lote Envasado</th>
                <th>Capacidad</th>

                {/* Control Hologramas */}
                <th>Rango Inicial</th>
                <th>Consumidos</th>
                <th>Saldo Disponible</th>

                {/* Cierre */}
                <th>V.Bo. Inspector</th>
                <th>Estado</th>

                {/* Acciones */}
                <th style={{ textAlign: "center" }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {hologramaRecords.map((rec, i) => {
                const totalInicial = rec.invInicialCant || 0;
                const totalSalida = rec.salidaCant || 0;
                const saldoActual = rec.invFinalCant !== undefined && rec.invFinalCant !== null 
                  ? rec.invFinalCant 
                  : totalInicial;

                return (
                  <tr key={i}>
                    <td style={{ fontWeight: 700, color: "var(--copper)" }}>{rec.idEnvasadoTerminado}</td>
                    <td>{rec.fechaMovimiento}</td>
                    <td style={{ fontWeight: 800 }}>{rec.marca}</td>
                    <td>{rec.loteEnvasado}</td>
                    <td>{rec.capacidad}</td>

                    <td>
                      <div style={{ fontWeight: 700, fontSize: "12.5px", color: "#1E293B" }}>
                        {rec.invInicialRango || "N/A"}
                      </div>
                      <div style={{ fontSize: "11px", color: "#64748B" }}>
                        {totalInicial} asignados
                      </div>
                    </td>

                    <td>
                      <div style={{ fontWeight: 600, fontSize: "12px", color: "#D97706" }}>
                        {rec.salidaRango || "Sin salidas"}
                      </div>
                      <div style={{ fontSize: "11px", color: "#64748B" }}>
                        {totalSalida} aplicados
                      </div>
                    </td>

                    <td>
                      <div style={{ fontWeight: 800, fontSize: "12.5px", color: "#059669" }}>
                        {rec.invFinalRango || rec.invInicialRango || "N/A"}
                      </div>
                      <div style={{ fontSize: "11px", color: "#059669", fontWeight: 700 }}>
                        {saldoActual} disponibles
                      </div>
                    </td>

                    <td style={{ fontSize: "11px" }}>{rec.inspector}</td>
                    <td>
                      <span className={`badge-status ${rec.status}`}>
                        {rec.status === "aprobado" ? "Aprobado" : rec.status === "revision" ? "En revisión" : "Borrador"}
                      </span>
                    </td>

                    <td style={{ textAlign: "center" }}>
                      <button
                        type="button"
                        onClick={() => handleOpenMovimientosModal(rec)}
                        style={{
                          background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
                          color: "#FFFFFF",
                          border: "none",
                          borderRadius: "8px",
                          padding: "6px 12px",
                          fontSize: "12px",
                          fontWeight: 700,
                          cursor: "pointer",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          boxShadow: "0 2px 6px rgba(5, 150, 105, 0.25)",
                          transition: "all 0.2s ease"
                        }}
                        onMouseEnter={e => {
                          e.currentTarget.style.transform = "translateY(-1px)";
                          e.currentTarget.style.boxShadow = "0 4px 10px rgba(5, 150, 105, 0.35)";
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow = "0 2px 6px rgba(5, 150, 105, 0.25)";
                        }}
                        title="Ver y registrar salidas de hologramas por rango"
                      >
                        <TrendingDownIcon style={{ fontSize: 16 }} />
                        <span>Movimientos de Hologramas</span>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* MODAL DE MOVIMIENTOS DE HOLOGRAMAS */}
        {isMovimientosModalOpen && selectedHolograma && createPortal(
          <div 
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(15, 23, 42, 0.65)",
              backdropFilter: "blur(6px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 999999,
              padding: "20px"
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) setIsMovimientosModalOpen(false);
            }}
          >
            <div 
              style={{
                backgroundColor: "#FFFFFF",
                borderRadius: "16px",
                width: "100%",
                maxWidth: "850px",
                maxHeight: "90vh",
                overflowY: "auto",
                boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.1)",
                display: "flex",
                flexDirection: "column"
              }}
              onClick={e => e.stopPropagation()}
            >
              {/* Header del Modal */}
              <div 
                style={{
                  padding: "18px 24px",
                  borderBottom: "1px solid #E2E8F0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
                  borderTopLeftRadius: "16px",
                  borderTopRightRadius: "16px",
                  color: "#FFFFFF"
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <div style={{ 
                    width: "40px", 
                    height: "40px", 
                    borderRadius: "10px", 
                    background: "rgba(255, 255, 255, 0.2)", 
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: "center" 
                  }}>
                    <ConfirmationNumberIcon style={{ color: "#FFFFFF", fontSize: 24 }} />
                  </div>
                  <div>
                    <h3 style={{ margin: 0, fontSize: "17px", fontWeight: 800 }}>
                      Movimientos de Hologramas (Salidas por Rango)
                    </h3>
                    <p style={{ margin: "2px 0 0 0", fontSize: "12.5px", opacity: 0.9 }}>
                      Ref: <strong>{selectedHolograma.idEnvasadoTerminado}</strong> | Marca: <strong>{selectedHolograma.marca}</strong> | Lote: <strong>{selectedHolograma.loteEnvasado}</strong> ({selectedHolograma.capacidad})
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setIsMovimientosModalOpen(false)}
                  style={{
                    background: "rgba(255, 255, 255, 0.2)",
                    border: "none",
                    borderRadius: "50%",
                    width: "32px",
                    height: "32px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: "#FFFFFF",
                    transition: "all 0.2s ease"
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = "rgba(255, 255, 255, 0.3)"}
                  onMouseLeave={e => e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)"}
                >
                  <CloseIcon style={{ fontSize: 18 }} />
                </button>
              </div>

              {/* Resumen del Balance de Hologramas */}
              <div style={{ padding: "16px 24px", background: "#F8FAFC", borderBottom: "1px solid #E2E8F0" }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: "12px" }}>
                  <div style={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: "10px", padding: "10px 14px" }}>
                    <div style={{ fontSize: "11px", fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>Rango Asignado Inicial</div>
                    <div style={{ fontSize: "14px", fontWeight: 800, color: "#1E293B", marginTop: "3px" }}>{selectedHolograma.invInicialRango || "N/A"}</div>
                    <div style={{ fontSize: "11px", color: "#64748B" }}>Total: {selectedHolograma.invInicialCant || 0} pzs</div>
                  </div>

                  <div style={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: "10px", padding: "10px 14px" }}>
                    <div style={{ fontSize: "11px", fontWeight: 700, color: "#D97706", textTransform: "uppercase" }}>Salidas Acumuladas</div>
                    <div style={{ fontSize: "14px", fontWeight: 800, color: "#D97706", marginTop: "3px" }}>{selectedHolograma.salidaCant || 0} pzs</div>
                    <div style={{ fontSize: "11px", color: "#64748B" }}>{selectedHolograma.salidaRango || "Sin registros"}</div>
                  </div>

                  <div style={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: "10px", padding: "10px 14px" }}>
                    <div style={{ fontSize: "11px", fontWeight: 700, color: "#DC2626", textTransform: "uppercase" }}>Mermas Acumuladas</div>
                    <div style={{ fontSize: "14px", fontWeight: 800, color: "#DC2626", marginTop: "3px" }}>{selectedHolograma.mermasCant || 0} pzs</div>
                    <div style={{ fontSize: "11px", color: "#64748B" }}>{selectedHolograma.mermasRango || "N/A"}</div>
                  </div>

                  <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: "10px", padding: "10px 14px" }}>
                    <div style={{ fontSize: "11px", fontWeight: 700, color: "#059669", textTransform: "uppercase" }}>Saldo Disponible Actual</div>
                    <div style={{ fontSize: "15px", fontWeight: 900, color: "#059669", marginTop: "3px" }}>
                      {selectedHolograma.invFinalCant !== undefined && selectedHolograma.invFinalCant !== null 
                        ? selectedHolograma.invFinalCant 
                        : (selectedHolograma.invInicialCant || 0)} pzs
                    </div>
                    <div style={{ fontSize: "11px", color: "#059669", fontWeight: 600 }}>
                      {selectedHolograma.invFinalRango || selectedHolograma.invInicialRango || "N/A"}
                    </div>
                  </div>
                </div>
              </div>

              {/* Contenido del Modal con la sección exacta solicitada */}
              <div style={{ padding: "24px", display: "flex", flexDirection: "column", gap: "24px" }}>

                {/* TARJETA IDÉNTICA A LA IMAGEN ADJUNTA */}
                <div 
                  style={{
                    backgroundColor: "#FFFFFF",
                    border: "1px solid #E2E8F0",
                    borderRadius: "14px",
                    padding: "24px",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.04)"
                  }}
                >
                  {/* Encabezado de la Tarjeta */}
                  <div style={{ display: "flex", alignItems: "flex-start", gap: "14px", marginBottom: "22px" }}>
                    <div 
                      style={{
                        width: "44px",
                        height: "44px",
                        borderRadius: "10px",
                        background: "#E6F4EA",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0
                      }}
                    >
                      <LayersIcon style={{ color: "#059669", fontSize: 24 }} />
                    </div>
                    <div>
                      <h4 style={{ margin: 0, fontSize: "16px", fontWeight: 800, color: "#0F172A" }}>
                        2. Consumo (Salidas), Mermas y Balance Final
                      </h4>
                      <p style={{ margin: "2px 0 0 0", fontSize: "13px", color: "#64748B" }}>
                        Registro de hologramas aplicados y mermas por daño
                      </p>
                    </div>
                  </div>

                  {/* Asistente opcional para cálculo automático */}
                  <div style={{ marginBottom: "18px", background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: "10px", padding: "12px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <span style={{ fontSize: "12.5px", fontWeight: 700, color: "#334155" }}>
                        ⚡ Asistente de cálculo por folios inicial y final
                      </span>
                      <button
                        type="button"
                        onClick={() => setUsarAsistente(!usarAsistente)}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#059669",
                          fontSize: "12px",
                          fontWeight: 700,
                          cursor: "pointer",
                          textDecoration: "underline"
                        }}
                      >
                        {usarAsistente ? "Ocultar Asistente" : "Mostrar Asistente"}
                      </button>
                    </div>

                    {usarAsistente && (
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr 2fr", gap: "12px", marginTop: "10px" }}>
                        <div>
                          <label style={{ display: "block", fontSize: "11px", fontWeight: 700, color: "#64748B", marginBottom: "4px" }}>Prefijo</label>
                          <input 
                            type="text" 
                            value={asistentePrefijo} 
                            onChange={e => handleCalcularRangoAsistente(e.target.value.toUpperCase(), asistenteFolioIni, asistenteFolioFin)}
                            placeholder="A"
                            style={{ width: "100%", padding: "8px 12px", borderRadius: "8px", border: "1px solid #CBD5E1", fontSize: "13px" }}
                          />
                        </div>
                        <div>
                          <label style={{ display: "block", fontSize: "11px", fontWeight: 700, color: "#64748B", marginBottom: "4px" }}>Folio Inicial</label>
                          <input 
                            type="text" 
                            value={asistenteFolioIni} 
                            onChange={e => handleCalcularRangoAsistente(asistentePrefijo, e.target.value, asistenteFolioFin)}
                            placeholder="Ej. 0123"
                            style={{ width: "100%", padding: "8px 12px", borderRadius: "8px", border: "1px solid #CBD5E1", fontSize: "13px" }}
                          />
                        </div>
                        <div>
                          <label style={{ display: "block", fontSize: "11px", fontWeight: 700, color: "#64748B", marginBottom: "4px" }}>Folio Final</label>
                          <input 
                            type="text" 
                            value={asistenteFolioFin} 
                            onChange={e => handleCalcularRangoAsistente(asistentePrefijo, asistenteFolioIni, e.target.value)}
                            placeholder="Ej. 0223"
                            style={{ width: "100%", padding: "8px 12px", borderRadius: "8px", border: "1px solid #CBD5E1", fontSize: "13px" }}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Campos requeridos (Fila 1: Rango Consumido y Cantidad Consumida) */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "18px", marginBottom: "16px" }}>
                    <div>
                      <label style={{ display: "block", fontSize: "13px", fontWeight: 700, color: "#0F172A", marginBottom: "6px" }}>
                        Rango Consumido (Aplicados)
                      </label>
                      <input 
                        type="text"
                        value={movRangoConsumido}
                        onChange={e => setMovRangoConsumido(e.target.value)}
                        placeholder="A0123 - A0223"
                        style={{
                          width: "100%",
                          padding: "12px 16px",
                          borderRadius: "10px",
                          border: "1px solid #CBD5E1",
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#0F172A",
                          outline: "none",
                          transition: "border-color 0.2s"
                        }}
                        onFocus={e => e.target.style.borderColor = "#059669"}
                        onBlur={e => e.target.style.borderColor = "#CBD5E1"}
                      />
                    </div>

                    <div>
                      <label style={{ display: "block", fontSize: "13px", fontWeight: 700, color: "#0F172A", marginBottom: "6px" }}>
                        Cantidad Consumida
                      </label>
                      <input 
                        type="number"
                        value={movCantidadConsumida}
                        onChange={e => setMovCantidadConsumida(e.target.value)}
                        placeholder="101"
                        style={{
                          width: "100%",
                          padding: "12px 16px",
                          borderRadius: "10px",
                          border: "1px solid #CBD5E1",
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#0F172A",
                          outline: "none",
                          transition: "border-color 0.2s"
                        }}
                        onFocus={e => e.target.style.borderColor = "#059669"}
                        onBlur={e => e.target.style.borderColor = "#CBD5E1"}
                      />
                    </div>
                  </div>

                  {/* Campos requeridos (Fila 2: Rango Mermas y Cantidad Mermada) */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "18px", marginBottom: "16px" }}>
                    <div>
                      <label style={{ display: "block", fontSize: "13px", fontWeight: 700, color: "#0F172A", marginBottom: "6px" }}>
                        Rango Mermas (Dañados)
                      </label>
                      <input 
                        type="text"
                        value={movRangoMermas}
                        onChange={e => setMovRangoMermas(e.target.value)}
                        placeholder="N/A"
                        style={{
                          width: "100%",
                          padding: "12px 16px",
                          borderRadius: "10px",
                          border: "1px solid #CBD5E1",
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#0F172A",
                          outline: "none",
                          transition: "border-color 0.2s"
                        }}
                        onFocus={e => e.target.style.borderColor = "#059669"}
                        onBlur={e => e.target.style.borderColor = "#CBD5E1"}
                      />
                    </div>

                    <div>
                      <label style={{ display: "block", fontSize: "13px", fontWeight: 700, color: "#0F172A", marginBottom: "6px" }}>
                        Cantidad Mermada
                      </label>
                      <input 
                        type="number"
                        value={movCantidadMermada}
                        onChange={e => setMovCantidadMermada(e.target.value)}
                        placeholder="0"
                        style={{
                          width: "100%",
                          padding: "12px 16px",
                          borderRadius: "10px",
                          border: "1px solid #CBD5E1",
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#0F172A",
                          outline: "none",
                          transition: "border-color 0.2s"
                        }}
                        onFocus={e => e.target.style.borderColor = "#059669"}
                        onBlur={e => e.target.style.borderColor = "#CBD5E1"}
                      />
                    </div>
                  </div>

                  {/* Fila 3: Observaciones sobre hologramas */}
                  <div style={{ marginBottom: "20px" }}>
                    <label style={{ display: "block", fontSize: "13px", fontWeight: 700, color: "#0F172A", marginBottom: "6px" }}>
                      Observaciones sobre hologramas
                    </label>
                    <textarea 
                      value={movObservaciones}
                      onChange={e => setMovObservaciones(e.target.value)}
                      placeholder="Hologramas aplicados e inventario actualizado."
                      rows={3}
                      style={{
                        width: "100%",
                        padding: "12px 16px",
                        borderRadius: "10px",
                        border: "1px solid #CBD5E1",
                        fontSize: "14px",
                        color: "#0F172A",
                        outline: "none",
                        resize: "vertical",
                        transition: "border-color 0.2s"
                      }}
                      onFocus={e => e.target.style.borderColor = "#059669"}
                      onBlur={e => e.target.style.borderColor = "#CBD5E1"}
                    />
                  </div>

                  {/* Botón de acción para registrar este movimiento */}
                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <button
                      type="button"
                      onClick={handleGuardarMovimiento}
                      style={{
                        padding: "10px 20px",
                        borderRadius: "8px",
                        border: "none",
                        background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
                        color: "#FFFFFF",
                        fontSize: "13px",
                        fontWeight: 700,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        boxShadow: "0 2px 6px rgba(5, 150, 105, 0.3)"
                      }}
                    >
                      <CheckCircleIcon style={{ fontSize: 18 }} />
                      <span>Registrar Movimiento de Salida</span>
                    </button>
                  </div>
                </div>

                {/* TABLA DE HISTORIAL DE MOVIMIENTOS REGISTRADOS PARA ESTE LOTE/RANGO */}
                <div style={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: "14px", padding: "20px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "14px" }}>
                    <HistoryIcon style={{ color: "#059669", fontSize: 20 }} />
                    <h4 style={{ margin: 0, fontSize: "15px", fontWeight: 800, color: "#1E293B" }}>
                      Historial de Movimientos de Salidas del Rango
                    </h4>
                  </div>

                  {(!selectedHolograma.movimientos || selectedHolograma.movimientos.length === 0) ? (
                    <div style={{ textAlign: "center", padding: "24px 16px", background: "#F8FAFC", borderRadius: "10px", border: "1px dashed #CBD5E1" }}>
                      <LocalOfferIcon style={{ fontSize: 32, color: "#94A3B8", marginBottom: "6px" }} />
                      <p style={{ margin: 0, fontSize: "13px", color: "#64748B", fontWeight: 600 }}>
                        {selectedHolograma.salidaCant && Number(selectedHolograma.salidaCant) > 0
                          ? `Salida inicial registrada: ${selectedHolograma.salidaRango} (${selectedHolograma.salidaCant} pzs). Registre nuevos consumos arriba para sumar al historial.`
                          : "Aún no se han registrado movimientos de salida para este lote. Ingrese los datos en el formulario superior."}
                      </p>
                    </div>
                  ) : (
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12.5px" }}>
                        <thead>
                          <tr style={{ background: "#F1F5F9", color: "#475569", borderBottom: "1px solid #CBD5E1", textAlign: "left" }}>
                            <th style={{ padding: "8px 10px" }}>Fecha</th>
                            <th style={{ padding: "8px 10px" }}>Rango Consumido</th>
                            <th style={{ padding: "8px 10px" }}>Cantidad</th>
                            <th style={{ padding: "8px 10px" }}>Rango Mermas</th>
                            <th style={{ padding: "8px 10px" }}>Mermas</th>
                            <th style={{ padding: "8px 10px" }}>Saldo Restante</th>
                            <th style={{ padding: "8px 10px" }}>Observaciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedHolograma.movimientos.map((mov, mIdx) => (
                            <tr key={mov.id || mIdx} style={{ borderBottom: "1px solid #E2E8F0" }}>
                              <td style={{ padding: "10px 10px", color: "#64748B", whiteSpace: "nowrap" }}>
                                {mov.fecha}
                              </td>
                              <td style={{ padding: "10px 10px", fontWeight: 700, color: "#0F172A" }}>
                                {mov.rangoConsumido}
                              </td>
                              <td style={{ padding: "10px 10px", fontWeight: 800, color: "#D97706" }}>
                                {mov.cantidadConsumida}
                              </td>
                              <td style={{ padding: "10px 10px", color: "#64748B" }}>
                                {mov.rangoMermas || "N/A"}
                              </td>
                              <td style={{ padding: "10px 10px", color: Number(mov.cantidadMermada) > 0 ? "#DC2626" : "#64748B", fontWeight: 600 }}>
                                {mov.cantidadMermada || 0}
                              </td>
                              <td style={{ padding: "10px 10px", color: "#059669", fontWeight: 700 }}>
                                {mov.saldoRestanteCant !== undefined ? `${mov.saldoRestanteCant} pzs` : "-"}
                              </td>
                              <td style={{ padding: "10px 10px", color: "#64748B", fontSize: "11.5px" }}>
                                {mov.observaciones}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

              </div>

              {/* Footer del Modal */}
              <div 
                style={{
                  padding: "14px 24px",
                  borderTop: "1px solid #E2E8F0",
                  display: "flex",
                  justifyContent: "flex-end",
                  background: "#F8FAFC",
                  borderBottomLeftRadius: "16px",
                  borderBottomRightRadius: "16px"
                }}
              >
                <button
                  type="button"
                  onClick={() => setIsMovimientosModalOpen(false)}
                  style={{
                    padding: "8px 18px",
                    borderRadius: "8px",
                    border: "1px solid #CBD5E1",
                    background: "#FFFFFF",
                    color: "#475569",
                    fontSize: "13px",
                    fontWeight: 600,
                    cursor: "pointer"
                  }}
                >
                  Cerrar
                </button>
              </div>

            </div>
          </div>,
          document.body
        )}

      </div>
    );
  }

  return (
    <div className="animate-fadeIn">
      <div className="history-section-header">
        <button onClick={() => setIsFormOpen(false)} className="btn-secondary-custom" style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 16px" }}>
          <ArrowBackIcon style={{ fontSize: 16 }} />
          <span>Volver al Historial</span>
        </button>
        <h2>Nuevo Registro de Inventario de Hologramas - Sección V</h2>
      </div>

      <div className="cards-container-grid">
        {/* ENLAZAR ENVASADO TERMINADO */}
        <section className="detail-card detail-card-accent-left-green">
          <header className="detail-card-header">
            <div className="detail-card-icon-wrapper-green">
              <LayersIcon />
            </div>
            <div className="detail-card-title-wrapper">
              <h2>Relación de Envasado & Identificación</h2>
              <p>Vincular holograma con lote terminado</p>
            </div>
          </header>

          <div className="custom-input-group">
            <select 
              value={idEnvasadoTerminado} 
              onChange={e => setIdEnvasadoTerminado(e.target.value)}
              className="custom-select-field"
              style={{ padding: "18px 16px 8px 16px" }}
            >
              <option value="manual">Ingreso Manual (Sin lote pre-registrado)</option>
              {terminadoRecords.map(r => (
                <option key={r.id} value={r.id}>
                  {r.id} - Lote: {r.loteEnvasado} ({r.marca} - {r.capacidad})
                </option>
              ))}
            </select>
            <label className="custom-input-label">Enlace Envasado Terminado (ID)</label>
          </div>

          <div className="species-grid" style={{ marginTop: 16 }}>
            <div className="custom-input-group">
              <input 
                type="date" 
                value={fechaMovimiento} 
                onChange={e => setFechaMovimiento(e.target.value)} 
                className="custom-input-field"
              />
              <label className="custom-input-label">Fecha del Movimiento</label>
            </div>

            <div className="custom-input-group">
              <input 
                type="text" 
                value={marca} 
                onChange={e => setMarca(e.target.value)} 
                className="custom-input-field"
                disabled={idEnvasadoTerminado !== "manual"}
              />
              <label className="custom-input-label">Marca Comercial</label>
            </div>
          </div>

          <div className="species-grid">
            <div className="custom-input-group">
              <input 
                type="text" 
                value={loteEnvasado} 
                onChange={e => setLoteEnvasado(e.target.value)} 
                className="custom-input-field"
                disabled={idEnvasadoTerminado !== "manual"}
              />
              <label className="custom-input-label">Lote de Envasado</label>
            </div>

            <div className="custom-input-group">
              <input 
                type="text" 
                value={capacidad} 
                onChange={e => setCapacidad(e.target.value)} 
                className="custom-input-field"
                disabled={idEnvasadoTerminado !== "manual"}
              />
              <label className="custom-input-label">Capacidad de la Botella</label>
            </div>
          </div>
        </section>

        {/* INVENTARIO INICIAL & ENTRADAS */}
        <section className="detail-card detail-card-accent-left-copper">
          <header className="detail-card-header">
            <div className="detail-card-icon-wrapper-copper">
              <ConfirmationNumberIcon />
            </div>
            <div className="detail-card-title-wrapper">
              <h2>Inventario Inicial & Entradas</h2>
              <p>Rangos numéricos correlativos asignados</p>
            </div>
          </header>

          <h3 style={{ fontSize: "14px", fontWeight: 700, color: "var(--text-color)", marginBottom: "8px" }}>INVENTARIO INICIAL</h3>
          <div className="species-grid">
            <div className="custom-input-group">
              <input 
                type="text" 
                value={invInicialRango} 
                onChange={e => setInvInicialRango(e.target.value)} 
                className="custom-input-field" 
                placeholder="Ej. A0001 - A0500"
              />
              <label className="custom-input-label">Rango Inicial</label>
            </div>

            <div className="custom-input-group">
              <input 
                type="number" 
                value={invInicialCant} 
                onChange={e => setInvInicialCant(e.target.value)} 
                className="custom-input-field" 
              />
              <label className="custom-input-label">Cantidad Inicial</label>
            </div>
          </div>

          <h3 style={{ fontSize: "14px", fontWeight: 700, color: "var(--agave-green)", marginBottom: "8px", marginTop: "20px" }}>ENTRADAS (NUEVA ASIGNACIÓN)</h3>
          <div className="species-grid">
            <div className="custom-input-group">
              <input 
                type="text" 
                value={entradaRango} 
                onChange={e => setEntradaRango(e.target.value)} 
                className="custom-input-field" 
                placeholder="Ej. B0001 - B1000"
              />
              <label className="custom-input-label">Rango Entrada</label>
            </div>

            <div className="custom-input-group">
              <input 
                type="number" 
                value={entradaCant} 
                onChange={e => setEntradaCant(e.target.value)} 
                className="custom-input-field" 
              />
              <label className="custom-input-label">Cantidad Entrada</label>
            </div>
          </div>
        </section>

        {/* SALIDAS, MERMAS E INVENTARIO FINAL */}
        <section className="detail-card detail-card-accent-left-green" style={{ gridColumn: "1 / -1" }}>
          <header className="detail-card-header">
            <div className="detail-card-icon-wrapper-green">
              <TrendingDownIcon />
            </div>
            <div className="detail-card-title-wrapper">
              <h2>Consumo (Salidas), Mermas y Balance Final</h2>
              <p>Control de uso de hologramas y adhesión física en botella</p>
            </div>
          </header>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "24px", marginTop: "12px" }}>
            {/* Column 1: Salidas */}
            <div>
              <h3 style={{ fontSize: "14px", fontWeight: 700, color: "var(--copper)", borderBottom: "1px solid var(--glass-border)", paddingBottom: "6px", marginBottom: "16px" }}>
                SALIDAS (APLICADOS)
              </h3>
              <div className="custom-input-group" style={{ marginBottom: 12 }}>
                <input 
                  type="text" 
                  value={salidaRango} 
                  onChange={e => setSalidaRango(e.target.value)} 
                  className="custom-input-field" 
                  placeholder="Ej. A0001 - A0450"
                />
                <label className="custom-input-label">Rango Consumido</label>
              </div>

              <div className="custom-input-group">
                <input 
                  type="number" 
                  value={salidaCant} 
                  onChange={e => setSalidaCant(e.target.value)} 
                  className="custom-input-field" 
                />
                <label className="custom-input-label">Cantidad Consumida</label>
              </div>
            </div>

            {/* Column 2: Mermas */}
            <div>
              <h3 style={{ fontSize: "14px", fontWeight: 700, color: "var(--status-draft-color)", borderBottom: "1px solid var(--glass-border)", paddingBottom: "6px", marginBottom: "16px" }}>
                MERMAS (DAÑADOS)
              </h3>
              <div className="custom-input-group" style={{ marginBottom: 12 }}>
                <input 
                  type="text" 
                  value={mermasRango} 
                  onChange={e => setMermasRango(e.target.value)} 
                  className="custom-input-field" 
                  placeholder="Ej. A0451 - A0453"
                />
                <label className="custom-input-label">Rango Mermas</label>
              </div>

              <div className="custom-input-group">
                <input 
                  type="number" 
                  value={mermasCant} 
                  onChange={e => setMermasCant(e.target.value)} 
                  className="custom-input-field" 
                />
                <label className="custom-input-label">Cantidad Mermada</label>
              </div>
            </div>

            {/* Column 3: Inventario Final */}
            <div>
              <h3 style={{ fontSize: "14px", fontWeight: 700, color: "var(--agave-green)", borderBottom: "1px solid var(--glass-border)", paddingBottom: "6px", marginBottom: "16px" }}>
                INVENTARIO FINAL
              </h3>
              <div className="custom-input-group" style={{ marginBottom: 12 }}>
                <input 
                  type="text" 
                  value={invFinalRango} 
                  onChange={e => setInvFinalRango(e.target.value)} 
                  className="custom-input-field" 
                  placeholder="Ej. A0454 - A0500"
                />
                <label className="custom-input-label">Rango Final</label>
              </div>

              <div className="custom-input-group">
                <input 
                  type="number" 
                  value={invFinalCant} 
                  onChange={e => setInvFinalCant(e.target.value)} 
                  className="custom-input-field" 
                />
                <label className="custom-input-label">Cantidad Final</label>
              </div>
            </div>
          </div>

          <div className="custom-input-group" style={{ marginTop: 24 }}>
            <textarea 
              value={observaciones} 
              onChange={e => setObservaciones(e.target.value)} 
              className="custom-input-field"
              style={{ height: "80px", resize: "none", padding: "14px" }}
              placeholder="Ej. Hologramas dañados en la aplicación del lote T-ENV-01. Mermas archivadas para revisión..."
            />
            <label className="custom-input-label">Observaciones</label>
          </div>
        </section>
      </div>

      <div className="button-row">
        <button type="button" className="btn-secondary-custom" onClick={() => setIsFormOpen(false)}>Volver</button>
        <button 
          type="button" 
          className="btn-primary-custom" 
          onClick={() => handleSaveHologramas("revision")}
        >
          <CheckCircleIcon style={{ fontSize: 16 }} />
          <span>Registrar Sección V</span>
        </button>
      </div>
    </div>
  );
};

export default ControlHologramasSection;
